import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Box,
  Tab,
  Tabs,
  CircularProgress
} from "@mui/material";
import {SaudiRiyal} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect } from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import { useDeleteSparePartMutation, useGetSparePartsQuery, useUpdateSparePartMutation } from "../../service/features/api";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import Pagination from "../../components/Panigation";
import { VEHICLE_TYPE } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const typeMapping = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
];

const ManageSpareParts = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Listings Management", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [valueTabs, setValueTabs] = useState(0);

  const tabTypes = typeMapping.map(type => getVehicleTypeLabel(type));

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const [deleteSparePart, { isLoading: isDeleting }] = useDeleteSparePartMutation();
  const [updateSparePart] = useUpdateSparePartMutation();

  const handleDelete = async () => {
    if (selectedId) {
      try {
        await deleteSparePart(selectedId).unwrap();
        toast.success("Spare part deleted successfully");
        setOpenDeleteModal(false);
      } catch (error) {
        toast.error("Failed to delete spare part");
      }
    }
  };

  const { data, isFetching } = useGetSparePartsQuery({
    page,
    limit: 10,
    search: searchTerm,
    type: typeMapping[valueTabs]
  });

  const rows = data?.data?.data || [];
  const paginator = data?.data?.paginator || {};

  return (
    <>
      <CommonBreadcrumb
        title="إدارة قطع الغيار / Spare Parts Management"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "إدارة قطع الغيار / Spare Parts Management" },
        ]}
      />

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              className="custom_tabs_links"
            >
              {tabTypes.map((label, index) => (
                <Tab key={label} label={label} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="بحث عن قطعة غيار... / Search spare part..."
            />
          </div>
          <div className="right_s">
            <Can module="Listings Management" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-spare-parts/add", { state: { defaultType: typeMapping[valueTabs] } })}
              >
                <AddIcon /> إضافة قطعة غيار / Add Spare Part
              </Button>
            </Can>
          </div>
        </div>

        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>تفاصيل قطعة الغيار / Spare Part Detail</TableCell>
                    <TableCell>تفاصيل الوكيل / Dealer Detail</TableCell>
                    <TableCell>السعر / Price</TableCell>
                    <TableCell>تاريخ الإنشاء / Created Date</TableCell>
                    <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                    <TableCell>مميز / Featured</TableCell>
                    <TableCell>إجراءات / Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isFetching ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length > 0 ? (
                    rows.map((row: any) => {
                      return (
                        <TableRow hover key={row._id}>
                          {/* Spare Part Detail */}
                          <TableCell>
                            <div className="user_block">
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <figure className="no_radius" style={{ margin: 0 }}>
                                  <img src={row.images?.[0] || "/static/images/placeholder.png"} alt={row.name} />
                                </figure>
                                {row.images?.length > 1 && (
                                  <span style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#e0e0e0",
                                    color: "#333",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}>
                                    +{row.images.length - 1}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p><b>{row.name}</b></p>
                              </div>
                            </div>
                          </TableCell>

                          {/* Dealer Detail */}
                          <TableCell>
                            <a
                              href="/manage-dealers/detail"
                              onClick={(e) => {
                                e.preventDefault();
                                // navigate("/manage-dealers/detail"); 
                              }}
                              className="user_block click_action">
                              <p>
                                <b>{row.dealer?.fullName || row.dealer?.name || "N/A"}</b>
                                <span>{row.dealer?.countryCode || ""}{row.dealer?.phone || "N/A"}</span>
                              </p>
                            </a>
                          </TableCell>

                          {/* Price */}
                          <TableCell>
                            <strong className="c_primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><SaudiRiyal size={16} />{row.discountPrice}</strong> <del><SaudiRiyal size={16} />{row.actualPrice}</del>
                          </TableCell>

                          <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>

                          <TableCell>
                            {canEdit ? (
                              <SwitchToggle
                                checked={!!row.isActive}
                                onChange={async () => {
                                  try {
                                    await updateSparePart({ id: row._id, isActive: !row.isActive }).unwrap();
                                    toast.success("Status updated successfully");
                                  } catch (error: any) {
                                    console.error("Update error", error);
                                    toast.error(error?.data?.message || "Failed to update status");
                                  }
                                }}
                              />
                            ) : (
                              <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                                {row.isActive ? "Enabled" : "Disabled"}
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            {canEdit ? (
                              <SwitchToggle
                                checked={!!row.isFeatured}
                                onChange={async () => {
                                  try {
                                    await updateSparePart({ id: row._id, isFeatured: !row.isFeatured }).unwrap();
                                    toast.success("Featured status updated successfully");
                                  } catch (error: any) {
                                    console.error("Update error", error);
                                    toast.error(error?.data?.message || "Failed to update featured status");
                                  }
                                }}
                              />
                            ) : (
                              <span className={`status_btn ${row.isFeatured ? "c_dark_info" : "c_dark_warning"}`}>
                                {row.isFeatured ? "Featured" : "Normal"}
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            <div className="table_actions v2">
                              <Can module="Listings Management" action="isView">
                                <Tooltip title="View" arrow>
                                  <IconButton onClick={() => navigate(`/manage-spare-parts/detail/${row._id}`)}>
                                    <img src="/static/images/eye_icon.svg" alt="View" />
                                  </IconButton>
                                </Tooltip>
                              </Can>

                              <Can module="Listings Management" action="isEdit" isDisabledMode={true}>
                                <Tooltip title="Edit" arrow>
                                  <IconButton onClick={() => navigate(`/manage-spare-parts/edit/${row._id}`)}>
                                    <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                  </IconButton>
                                </Tooltip>
                              </Can>

                              <Can module="Listings Management" action="isDelete" isDisabledMode={true}>
                                <Tooltip title="Delete" arrow>
                                  <IconButton onClick={() => {
                                    setSelectedId(row._id);
                                    setOpenDeleteModal(true);
                                  }}>
                                    <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                  </IconButton>
                                </Tooltip>
                              </Can>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        لا توجد قطع غيار / No spare parts found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer >

            {paginator && paginator.pageCount > 1 && (
              <Pagination
                module={rows}
                page={page}
                setPage={setPage}
                onPageChange={(newP: number) => setPage(newP)}
                totalPages={paginator.pageCount}
                totalCount={paginator.itemCount}
              />
            )}
          </CustomTabPanel>
        ))}

        <DeleteConfirmModal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
          message="Are you sure you want to delete this spare part?"
        />
      </div >
    </>
  );
};

export default ManageSpareParts;
